import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, X, MapPin } from 'lucide-react'
import { codeToSlug, getStateRow, formatLossUSD } from '@/lib/ic3'

const DISMISS_KEY = 'sd:detect-banner-dismissed'
const DETECTED_KEY = 'sd:detected-state'

type Detected = { code: string; slug: string; name: string }

/**
 * Soft, dismissible banner that suggests the visitor's state.
 * Resolves region via a free IP geolocation endpoint; if that fails or the
 * region isn't a US state we know, the banner stays hidden.
 */
export default function StateDetectBanner() {
  const [detected, setDetected] = useState<Detected | null>(null)
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(DISMISS_KEY)) return
    setDismissed(false)

    const cached = window.localStorage.getItem(DETECTED_KEY)
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as Detected
        if (parsed?.code && parsed?.slug) {
          setDetected(parsed)
          return
        }
      } catch {
        // fall through to refetch
      }
    }

    let cancelled = false
    fetch('https://ipapi.co/json/', { method: 'GET' })
      .then(r => (r.ok ? r.json() : null))
      .then((data: { country_code?: string; region_code?: string } | null) => {
        if (cancelled || !data) return
        if (data.country_code !== 'US' || !data.region_code) return
        const code = data.region_code.toUpperCase()
        const row = getStateRow(code)
        const slug = codeToSlug(code)
        if (!row || !slug) return
        const next: Detected = { code, slug, name: row.name }
        window.localStorage.setItem(DETECTED_KEY, JSON.stringify(next))
        setDetected(next)
      })
      .catch(() => {
        // silently ignore; banner just won't show
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (dismissed || !detected) return null

  const row = getStateRow(detected.code)

  return (
    <div className="border-b border-violet-100 bg-violet-50/70 text-slate-700">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-2.5 text-sm">
        <div className="flex min-w-0 items-center gap-2.5">
          <MapPin className="h-4 w-4 shrink-0 text-violet-500" />
          <p className="truncate">
            Looks like you're in <span className="font-semibold text-slate-900">{detected.name}</span>
            {row && (
              <span className="text-slate-500">
                {' '}
                · residents reported {formatLossUSD(row.losses)} in fraud losses last year
              </span>
            )}
            .
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/states/$slug"
            params={{ slug: detected.slug }}
            className="group inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-500"
          >
            See {detected.name} fraud trends
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <button
            type="button"
            onClick={() => {
              setDismissed(true)
              window.localStorage.setItem(DISMISS_KEY, '1')
            }}
            className="rounded-full p-1 text-slate-400 hover:bg-violet-100 hover:text-slate-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
